import { getHierarchicGraph } from 'graph/hierarchicGraph';
import { getAggregatedGraph } from 'graph/aggregatedGraph';
import type { ComponentDataModel } from 'graph/graphItemTypes';
import type { Node } from 'graph/types';
import type { GraphItemModel } from 'graph/GraphItem';
import type { ArdoqId } from '@ardoq/api-types';
import type { Tree } from './types';

const nodeToHierarchyNode = (node: Node<ComponentDataModel>): Tree<ArdoqId> => {
  // Filter children to only include component nodes
  const componentChildren = node.children.filter(node => node.isComponent());

  return {
    data: node.dataModel._id,
    children: componentChildren.map(childNode =>
      nodeToHierarchyNode(childNode)
    ),
  };
};

export const getNodes = (
  workspaceId: ArdoqId,
  hasGroupingRules: boolean
): Node<GraphItemModel>[] => {
  if (!hasGroupingRules) {
    // No grouping rules - use hierarchic graph
    const graph = getHierarchicGraph().getGraphByWorkspace({
      workspaceId,
      maxDegreesIncoming: 0,
      maxDegreesOutgoing: 0,
    });

    return Array.from(graph.nodes.values());
  }

  // Has grouping rules - use aggregated graph
  const graph = getAggregatedGraph().getFullGraph();
  return graph.rootNodes.flatten();
};

export const extractHierarchyFromGraph = (
  nodes: Node<GraphItemModel>[]
): Tree<ArdoqId>[] => {
  // Find all component nodes that have component children
  // Include components with no parent OR components whose parent is a workspace
  const rootParentComponentNodes = nodes.filter(
    (node): node is Node<ComponentDataModel> =>
      node.isComponent() &&
      node.children.some(node => node.isComponent()) &&
      (!node.parent || node.parent.isWorkspace())
  );

  return rootParentComponentNodes.map(node => nodeToHierarchyNode(node));
};
